import * as React from 'react';
import _ from 'lodash';
import styled, { css } from 'styled-components';
import { sortOptions } from './TimelineOptions.logic';
import {
  TimelineOptionsType,
  TimelineOptionsItemsType
} from './TimelineOptions.types';
import { Text } from '../Text/Text';

export interface Props {
  first?: boolean;
  middle?: boolean;
  last?: boolean;
}

const chainMixin = css`
  content: '';
  position: absolute;
  width: 1px;
  height: 28px;
  left: -1px;
`;

const TimelineOptionsElement = styled.div<Props>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
  width: 100%;
  position: relative;
  padding: 11px 24px;
  color: ${({ theme }) => theme.colors.font3};
  &:hover {
    background: ${({ theme }) => theme.colors.timeline.optionsHover};
  }
  ${({ first, last, theme }) =>
    (first || last) &&
    `
      &:after {
        ${chainMixin};
        border-right: 1px solid ${theme.colors.timeline.lineColor};
        ${last ? 'top: -10px' : 'bottom: 10px'};
        left: 0px;
      }
`};
  ${({ middle, theme }) =>
    middle &&
    `
      &:before {
        ${chainMixin};
        border-right: 1px solid ${theme.colors.timeline.lineColor};
        top: -10px;
        left: 0px;
      }

      &:after {
        ${chainMixin};
        border-right: 1px solid ${theme.colors.timeline.lineColor};
        bottom: 10px;
        left: 0px;
      }
    `};
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ActionItem = styled.div`
  margin-left: 16px;
  font-size: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.font1};
`;

const Indicator = styled.div`
  &:after {
    content: '';
    position: absolute;
    left: -4px;
    width: 9px;
    height: 9px;
    background: #5a5b61;
    border: 1px solid ${({ theme }) => theme.colors.white};
    border-radius: 50%;
    top: -20px;
    bottom: 0;
    margin: auto;
  }
`;

const BackupInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

const TimelineLabel = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.timeline.label};
`;

const TimelineName = styled(Text)`
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.timeline.name};
`;

const TimelineOptions = (props: TimelineOptionsType) => {
  const { items }: { items: TimelineOptionsItemsType[] } = props;

  return (
    <div className="timeline-options">
      {sortOptions(items).map(
        ({ creationDateTime, name, actions }, actionIndex) => {
          const createDate = new Date(creationDateTime);
          const label = `${createDate.getDate()}.${createDate.getMonth() + 1}`;
          const isFirstOption = _.isEqual(actionIndex, 0);
          const isLastOption = actionIndex === items.length - 1;

          return (
            <TimelineOptionsElement
              first={isFirstOption}
              middle={!isFirstOption && !isLastOption}
              last={isLastOption}
              key={creationDateTime}
            >
              <ActionsContainer>
                <Indicator />
                <BackupInfoContainer>
                  <TimelineName>{name}</TimelineName>
                  <TimelineLabel>{label}</TimelineLabel>
                </BackupInfoContainer>
              </ActionsContainer>

              <ActionsContainer>
                {actions.map(action => (
                  <ActionItem
                    role="button"
                    tabIndex={0}
                    onClick={() => action.triggerFunction(actionIndex)}
                    key={action.name}
                  >
                    {action.name}
                  </ActionItem>
                ))}
              </ActionsContainer>
            </TimelineOptionsElement>
          );
        }
      )}
    </div>
  );
};

export default TimelineOptions;
